import i18next from "i18next";

import { HTTP_STATUS_CODE, INTERNAL_ERROR_CODE } from "@/shared/constants/http";
import type { UnifiedError } from "@/shared/types/api";

export interface ErrorPresentation {
  title: string;
  description?: string;
}

function dedupeDescription(title: string, description?: string): string | undefined {
  const normalizedDescription = description?.trim();
  if (!normalizedDescription || normalizedDescription === title) {
    return undefined;
  }

  return normalizedDescription;
}

function translateReason(reason?: string): string | undefined {
  if (!reason) {
    return undefined;
  }

  const translated = i18next.t(reason, { ns: "errors", defaultValue: "" }).trim();
  return translated || undefined;
}

export function getErrorPresentation(error: UnifiedError): ErrorPresentation {
  if (error.status === HTTP_STATUS_CODE.BAD_REQUEST && error.reason === "VALIDATOR") {
    return {
      title: "请求参数有误",
      description: "当前筛选条件或请求参数无效，请刷新页面或重置筛选条件后重试。",
    };
  }

  if (error.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
    return {
      title: "登录状态已失效",
      description: "请重新登录后再试。",
    };
  }

  if (error.status && error.status >= HTTP_STATUS_CODE.SERVER_ERROR) {
    return {
      title: "服务器开小差了",
      description: "服务暂时不可用，请稍后再试。",
    };
  }

  if (error.code === INTERNAL_ERROR_CODE.TIMEOUT) {
    return {
      title: "请求超时",
      description: "服务器响应较慢，请稍后重试。",
    };
  }

  if (error.code === INTERNAL_ERROR_CODE.NETWORK) {
    return {
      title: "网络连接异常",
      description: "请检查网络连接后重试。",
    };
  }

  const translatedReason = translateReason(error.reason);
  const fallbackMessage =
    error.message?.trim() || i18next.t("REQUEST_FAILED", { ns: "errors" });

  return {
    title: translatedReason || fallbackMessage,
    description: dedupeDescription(translatedReason || fallbackMessage, fallbackMessage),
  };
}
